import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiService } from "../../config";
import useUserStore from "../../store/user";
import { nomeDaTela, perfilDaRota } from "./rotas";

type Config = {
  intervaloSegundos: number;
  pedirLocalizacao: boolean;
  localizacaoObrigatoria: boolean;
};

type Posicao = { lat: number; lon: number; accuracy: number };
type StatusGeo = "granted" | "denied" | "prompt" | "unsupported" | "error";

const AVISO_KEY = "gr-aviso-localizacao-v1";

const lerPosicao = (): Promise<{ status: StatusGeo; pos?: Posicao }> =>
  new Promise((resolve) => {
    if (!("geolocation" in navigator)) {
      resolve({ status: "unsupported" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ status: "granted", pos: { lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy } }),
      (err) => resolve({ status: err.code === err.PERMISSION_DENIED ? "denied" : "error" }),
      // posição de até 2 min serve: o navegador não liga o GPS a cada batimento
      { enableHighAccuracy: false, maximumAge: 120_000, timeout: 15_000 },
    );
  });

/**
 * Presença no painel (logs de acesso, 01/10/2026): avisa o backend a cada minuto e a cada
 * troca de tela qual tela está aberta e, para a equipe interna, a posição do navegador.
 * Só manda com a aba visível, para o tempo de uso ser tempo de uso de verdade.
 * Falha de rede aqui nunca aparece para o usuário.
 */
export const PresenceTracker = () => {
  const { user } = useUserStore();
  const location = useLocation();
  const [config, setConfig] = useState<Config | null>(null);
  const [statusGeo, setStatusGeo] = useState<StatusGeo | null>(null);
  const enviando = useRef(false);
  const token = user?.token;

  const enviar = useCallback(async () => {
    if (!token || enviando.current || document.visibilityState !== "visible") return;
    enviando.current = true;
    try {
      let geo: { status: StatusGeo; pos?: Posicao } | null = null;
      if (config?.pedirLocalizacao) {
        geo = await lerPosicao();
        setStatusGeo(geo.status);
      }
      const rota = window.location.pathname;
      const resp = await ApiService.post(
        "/presence",
        {
          route: rota,
          screen: nomeDaTela(rota),
          profileRef: perfilDaRota(rota),
          lat: geo?.pos?.lat ?? null,
          lon: geo?.pos?.lon ?? null,
          accuracy: geo?.pos?.accuracy ?? null,
          geoStatus: geo?.status ?? null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const novo: Config | undefined = resp.data?.data;
      if (novo) {
        setConfig((atual) =>
          atual &&
          atual.pedirLocalizacao === novo.pedirLocalizacao &&
          atual.localizacaoObrigatoria === novo.localizacaoObrigatoria &&
          atual.intervaloSegundos === novo.intervaloSegundos
            ? atual
            : novo,
        );
      }
    } catch {
      // presença é melhor esforço
    } finally {
      enviando.current = false;
    }
  }, [token, config?.pedirLocalizacao]);

  // aviso único antes do navegador perguntar pela localização
  useEffect(() => {
    if (!config?.pedirLocalizacao) return;
    try {
      if (localStorage.getItem(AVISO_KEY)) return;
      localStorage.setItem(AVISO_KEY, "1");
    } catch {
      // sem localStorage: avisa de novo na próxima vez, sem problema
    }
    toast.info(
      "Por segurança das contas dos clientes, o GeoRanking registra a localização do aparelho enquanto você usa o painel.",
      { autoClose: 10_000 },
    );
  }, [config?.pedirLocalizacao]);

  // a cada troca de tela (e no primeiro carregamento)
  useEffect(() => {
    enviar();
  }, [location.pathname, enviar]);

  // batimento + volta para a aba
  useEffect(() => {
    const ms = Math.max(30, config?.intervaloSegundos ?? 60) * 1000;
    const id = window.setInterval(enviar, ms);
    const aoVoltar = () => {
      if (document.visibilityState === "visible") enviar();
    };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [enviar, config?.intervaloSegundos]);

  const bloqueado =
    config?.localizacaoObrigatoria && (statusGeo === "denied" || statusGeo === "unsupported");
  if (!bloqueado) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="gr-geo-titulo"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(32,33,36,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div style={{ background: "#fff", borderRadius: 12, maxWidth: 440, padding: 24, fontFamily: "inherit" }}>
        <h2 id="gr-geo-titulo" style={{ margin: "0 0 8px", fontSize: 20 }}>
          Permita a localização para continuar
        </h2>
        <p style={{ margin: "0 0 12px", color: "#5f6368", lineHeight: 1.5 }}>
          Para proteger as contas dos clientes, o painel só funciona com a localização do aparelho ligada.
        </p>
        <p style={{ margin: "0 0 16px", color: "#5f6368", lineHeight: 1.5 }}>
          Clique no cadeado ao lado do endereço do site, em <strong>Localização</strong> escolha{" "}
          <strong>Permitir</strong> e recarregue a página.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ background: "#1264C7", color: "#fff", border: 0, borderRadius: 8, padding: "10px 16px", cursor: "pointer" }}
        >
          Já permiti, recarregar
        </button>
      </div>
    </div>
  );
};
